@use 'src/globals/main';

pn-radio-button {
  position: relative;
  display: inline-block;
}

pn-radio-button .pn-radio {
  position: relative;
  display: flex;
  gap: 0.75em;

  &-label {
    color: main.$label-color;
  }

  &-helpertext {
    color: main.$helpertext-color;
  }

  &-content {
    margin: 0;
    flex: 1 1 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &-container {
    display: flex;
    gap: 0.75em;
    padding: 0;
  }

  &-outer {
    background-color: main.$input-background;
    border: main.$input-border;
    border-radius: 50%;
    height: 1.5em;
    width: 1.5em;
    display: flex;
    justify-content: center;
    align-items: center;

    @include main.pn-focus-base;
    @include main.pn-transition('border-color, background-color, outline-color');
  }

  &-inner {
    height: 0.75em;
    width: 0.75em;
    background-color: main.$input-button-background-box-active;
    border-radius: 50%;
    transform: scale(0);
    transform-origin: center center;
    @include main.pn-transition('background-color, transform');
  }

  &[data-tile] {
    padding: 1em;
    border: main.$input-border;
    border-radius: main.$border-radius;
    background-color: main.$input-background;

    @include main.pn-focus-base;
    @include main.pn-transition('border-color, background-color, outline-color');

    .pn-radio-outer {
      outline: none;
    }
  }

  &[data-invalid] {
    border-color: main.$border-color-error;
    .pn-radio-outer {
      border-color: main.$border-color-error;
    }
    .pn-radio-inner {
      background-color: main.$input-button-active-error;
    }
  }
}

pn-radio-button > input[type='radio'] {
  cursor: pointer;
  opacity: 0;
  position: absolute;
  margin: 0;
  z-index: 1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  -webkit-tap-highlight-color: transparent;

  &[aria-describedby] + .pn-radio[data-tile] .pn-radio-label {
    font-weight: 500;
  }

  &:hover + .pn-radio {
    &[data-tile] {
      border-color: main.$border-color-default-hover;
      background-color: main.$input-button-background-hover;
      .pn-radio-outer {
        background-color: main.$input-button-background-hover;
      }
      &[data-invalid] {
        border-color: main.$border-color-error;
        background-color: main.$input-background-error-hover;
        .pn-radio-outer {
          background-color: main.$input-background-error-hover;
        }
      }
    }
    &[data-invalid] {
      .pn-radio-outer {
        border-color: main.$border-color-error;
        background-color: main.$input-background-error-hover;
      }
    }

    .pn-radio-outer {
      border-color: main.$input-button-background-box-active;
      background-color: main.$input-button-background-hover;
    }
  }

  &:checked:hover + .pn-radio {
    &[data-tile] {
      &[data-invalid] {
        border-color: main.$input-button-active-error-hover;
      }
    }
    &[data-invalid] > .pn-radio-container > .pn-radio-outer {
      border-color: main.$input-button-active-error-hover;
    }
  }

  &:disabled:hover {
    + .pn-radio {
      &[data-invalid] {
        border-color: main.$border-color-disabled;
      }
      > .pn-radio-container > .pn-radio-outer {
        background-color: main.$input-background-disabled;
      }
    }
  }
  &:disabled:checked:hover {
    + .pn-radio {
      &[data-invalid] {
        border-color: main.$border-color-disabled;
        > .pn-radio-container > .pn-radio-outer {
          background-color: main.$input-background-disabled;
          > .pn-radio-inner {
            background-color: main.$input-button-disabled;
          }
        }
      }
      > .pn-radio-container > .pn-radio-outer {
        background-color: main.$input-background-disabled;
      }
    }
  }

  &:focus-visible + .pn-radio .pn-radio-outer {
    @include main.pn-focus-visible;
  }

  &:focus-visible + .pn-radio[data-invalid] .pn-radio-outer {
    @include main.pn-focus-visible(main.$warning);
  }

  &:focus-visible + .pn-radio[data-tile] {
    @include main.pn-focus-visible(main.$border-color-default-focus, null, main.$border-color-default-focus);
    &[data-invalid] {
      @include main.pn-focus-visible(main.$input-button-active-error, null, main.$input-button-active-error);
    }
    > .pn-radio-container > .pn-radio-outer {
      outline: none;
    }
  }

  &:checked + .pn-radio {
    &[data-tile] {
      border-color: main.$border-color-default-active;
      background-color: main.$input-button-background-active;
      &[data-invalid] {
        border-color: main.$border-color-error-active;
        background-color: main.$input-background-error-active;
      }
    }
    > .pn-radio-container > .pn-radio-outer {
      border-color: main.$border-color-default-active;
      > .pn-radio-inner {
        transform: scale(1);
        background-color: main.$input-button-background-box-active;
      }
    }
    &[data-invalid] {
      > .pn-radio-container > .pn-radio-outer {
        border-color: main.$border-color-error;
        > .pn-radio-inner {
          background-color: main.$border-color-error;
        }
      }
    }
  }

  &:disabled {
    cursor: not-allowed;
    &:active + .pn-radio {
      outline: none;
    }
  }

  &:disabled + .pn-radio {
    &[data-tile],
    &[data-tile][data-invalid] {
      background-color: main.$input-background-disabled;
      border-color: main.$border-color-disabled;
      > pn-icon > .pn-icon-svg path {
        fill: main.$input-color-disabled;
      }
      > .pn-radio-content > .pn-radio-label {
        color: main.$input-color-disabled;
      }
    }

    > .pn-radio-container > .pn-radio-outer {
      background-color: main.$input-background-disabled;
      border-color: main.$input-border-disabled;
      outline: none;
      > .pn-radio-inner {
        background-color: main.$input-button-disabled;
      }
    }
  }

  &:disabled:checked + .pn-radio {
    &[data-invalid],
    &[data-tile],
    &[data-tile][data-invalid] {
      > .pn-radio-container > .pn-radio-outer {
        background-color: main.$input-background-disabled;
        border-color: main.$input-border-disabled;
        outline: none;
        > .pn-radio-inner {
          background-color: main.$input-button-disabled;
        }
      }
    }
  }
}
